import { useState, useMemo } from 'react';
import { useFuncionarios } from '@/context/FuncionariosContext';
import type { Funcionario } from '@/context/FuncionariosContext';
import { useSiadap } from '@/context/SiadapContext';
import { calcularProgressao, calcularPontosCiclo, calcularLimiarFuncionario, aplicaRegraEspecial18Anos, PONTOS_REGRA_18_ANOS, PONTOS_PARA_PROGRESSAO_2023_2024, type AvaliacaoSiadap, type PontosEscalasConfig, type PeriodoEscala } from '@/utils/progressaoUtils';
import { useAppSettings } from '@/context/AppSettingsContext';
import { useAuth } from '@/context/AuthContext';
import FilterBar from './components/FilterBar';
import FuncionariosTable from './components/FuncionariosTable';
import CarreiraGroup from './components/CarreiraGroup';
import RegistarProgressaoModal from './components/RegistarProgressaoModal';
import ExportPdfModal from './components/ExportPdfModal';
import EmailAlertModal from './components/EmailAlertModal';
import AlertasAutomaticosModal from './components/AlertasAutomaticosModal';
import EvolucaoPontosChart from './components/EvolucaoPontosChart';

export default function HomePage() {
  const { funcionarios: allFuncionarios, loading } = useFuncionarios();
  const { registosSiadap, registosHistorico, progressoes, pontosDescontadosPorFuncionario, loadingSiadap, reloadSiadap } = useSiadap();
  const { settings, loading: loadingSettings } = useAppSettings();
  const { profile } = useAuth();
  const [search, setSearch] = useState('');
  const [carreiraFilter, setCarreiraFilter] = useState('');
  const [sexoFilter, setSexoFilter] = useState('');
  const [avaliacaoFilter, setAvaliacaoFilter] = useState('');
  const [elegibilidadeFilter, setElegibilidadeFilter] = useState<'todos' | 'elegiveis' | 'proximos' | 'em_progresso'>('todos');
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [dismissedDuplosIds, setDismissedDuplosIds] = useState<Set<string>>(new Set());
  const [notifCollapsed, setNotifCollapsed] = useState(false);
  const [notifDuplosCollapsed, setNotifDuplosCollapsed] = useState(false);
  const [progressaoModal, setProgressaoModal] = useState<Funcionario | null>(null);
  const [exportModal, setExportModal] = useState(false);
  const [emailModal, setEmailModal] = useState(false);
  const [alertasModal, setAlertasModal] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [viewMode, setViewMode] = useState<'lista' | 'carreira'>('lista');

  const pontosMap = useMemo<Partial<Record<AvaliacaoSiadap, number>>>(() => ({
    Excelente: settings.pontos_excelente,
    'Muito Bom': settings.pontos_muito_bom,
    Bom: settings.pontos_bom,
    Regular: settings.pontos_regular,
    Inadequado: settings.pontos_inadequado,
  }), [settings]);

  // Escalas completas para as 3 épocas — vindas das definições de administração
  const escalas = useMemo<PontosEscalasConfig>(() => ({
    pos2025: {
      Excelente: settings.pontos_excelente,
      'Muito Bom': settings.pontos_muito_bom,
      Bom: settings.pontos_bom,
      Regular: settings.pontos_regular,
      Inadequado: settings.pontos_inadequado,
    },
    p2023_2024: {
      Excelente: settings.pontos_excelente_2023,
      'Muito Bom': settings.pontos_muito_bom_2023,
      Bom: settings.pontos_bom_2023,
      Regular: settings.pontos_regular_2023,
      Inadequado: settings.pontos_inadequado_2023,
    },
    pre2023: {
      Excelente:  settings.pontos_excelente_pre2023,
      Relevante:  settings.pontos_relevante_pre2023,
      Adequado:   settings.pontos_adequado_pre2023,
      Inadequado: settings.pontos_inadequado_pre2023,
      // aliases de compatibilidade
      'Muito Bom': settings.pontos_relevante_pre2023,
      Bom:         settings.pontos_adequado_pre2023,
      Regular:     settings.pontos_adequado_pre2023,
    },
  }), [settings]);



  const carreiras = useMemo(
    () => Array.from(new Set(allFuncionarios.map((f) => f.carreira))).sort(),
    [allFuncionarios]
  );

  const pontosPorFuncionario = useMemo(() => {
    const mapa: Record<string, number> = {};
    allFuncionarios.forEach((f) => {
      const siadapDoFunc = registosSiadap.filter((r) => r.funcionarioId === f.id);
      const historicoDoFunc = registosHistorico.filter((r) => r.funcionarioId === f.id);
      // Usa os descontos das progressões como base, e adiciona manuais apenas se forem realmente adicionais
      const descontosProgressoes = pontosDescontadosPorFuncionario[f.id] ?? 0;
      const descontosManuais = f.pontosDescontados ?? 0;
      let pontosDescontados: number;
      if (descontosManuais > descontosProgressoes) {
        // O campo inclui progressões + manuais — usar como total
        pontosDescontados = descontosManuais;
      } else if (descontosManuais === descontosProgressoes) {
        // Campo preenchido com os mesmos descontos das progressões — não duplicar
        pontosDescontados = descontosProgressoes;
      } else {
        // Manuais adicionais — somar aos descontos das progressões
        pontosDescontados = descontosProgressoes + descontosManuais;
      }
      // Exceção PREPAV: se o toggle estiver ativo, os pontos manuais são SEMPRE somados
      const isPrepav = f.excecaoPrepav === true;
      const temRegistos = siadapDoFunc.length > 0 || historicoDoFunc.length > 0;
      // Pontos manuais: sempre se PREPAV; só se sem registos caso contrário
      // Pontos manuais são SEMPRE somados (independentemente de PREPAV ou ter registos SIADAP)
      const pontosManuais = f.pontosAtuais ?? 0;
      mapa[f.id] = calcularPontosCiclo(
        siadapDoFunc,
        historicoDoFunc,
        pontosDescontados,
        pontosManuais,
        pontosMap,
        undefined,
        escalas
      );
    });
    return mapa;
  }, [allFuncionarios, registosSiadap, registosHistorico, pontosMap, escalas, pontosDescontadosPorFuncionario]);

  /** Limiar de progressão calculado dinamicamente por funcionário */
  const limiarPorFuncionario = useMemo(() => {
    const mapa: Record<string, number> = {};
    const limiarPos2025 = settings.pontos_progressao        || 8;
    const limiarPre2023 = settings.pontos_progressao_pre2025 || 10;
    const limiar2023_24 = settings.pontos_progressao_2023    || 8;
    allFuncionarios.forEach((f) => {
      const siadapDoFunc = registosSiadap.filter((r) => r.funcionarioId === f.id);
      const historicoDoFunc = registosHistorico.filter((r) => r.funcionarioId === f.id);
      const { limiar } = calcularLimiarFuncionario(
        siadapDoFunc,
        historicoDoFunc,
        limiarPos2025,
        limiarPre2023,
        limiar2023_24,
        escalas
      );
      mapa[f.id] = limiar;
    });
    return mapa;
  }, [allFuncionarios, registosSiadap, registosHistorico, settings.pontos_progressao, settings.pontos_progressao_pre2025, settings.pontos_progressao_2023, escalas]);

  /**
   * Regra especial 2024: funcionários com 18+ anos de serviço e 6 pontos têm direito a progressão.
   * O limiar efetivo para estes funcionários é 6 pts (em vez de 8).
   * Só se aplica se o limiar NORMAL do funcionário for > 6 (8 ou 10 pts).
   */
  const regraEspecialPorFuncionario = useMemo(() => {
    const mapa: Record<string, boolean> = {};
    allFuncionarios.forEach((f) => {
      const pontos = pontosPorFuncionario[f.id] ?? 0;
      const limiarNormal = limiarPorFuncionario[f.id] ?? PONTOS_PARA_PROGRESSAO_2023_2024;
      // Regra dos 18 anos só aplica se NUNCA progrediu — fonte de verdade: tabela progressoes
      const jaProgrediu = (pontosDescontadosPorFuncionario[f.id] ?? 0) > 0;
      mapa[f.id] = aplicaRegraEspecial18Anos(f.dataEntradaFuncaoPublica, pontos, jaProgrediu, 2024, limiarNormal);
    });
    return mapa;
  }, [allFuncionarios, pontosPorFuncionario, limiarPorFuncionario, pontosDescontadosPorFuncionario]);

  /** Mapa de funcionários com exceção PREPAV ativa */
  const prepavPorFuncionario = useMemo(() => {
    const mapa: Record<string, boolean> = {};
    allFuncionarios.forEach((f) => {
      mapa[f.id] = f.excecaoPrepav === true;
    });
    return mapa;
  }, [allFuncionarios]);

  /** Mapa de funcionários que já tiveram progressão com Acelerador Remuneratório */
  const funcionariosComAcelerador = useMemo(() => {
    const set = new Set<string>();
    progressoes.forEach((p) => {
      if (p.aceleradorRemuneratorio) set.add(p.funcionarioId);
    });
    return set;
  }, [progressoes]);

  /** Período de escala por funcionário (para indicador visual) */
  const periodoPorFuncionario = useMemo(() => {
    const mapa: Record<string, PeriodoEscala> = {};
    const limiarPos2025 = settings.pontos_progressao        || 8;
    const limiarPre2023 = settings.pontos_progressao_pre2025 || 10;
    const limiar2023_24 = settings.pontos_progressao_2023    || 8;
    allFuncionarios.forEach((f) => {
      const siadapDoFunc = registosSiadap.filter((r) => r.funcionarioId === f.id);
      const historicoDoFunc = registosHistorico.filter((r) => r.funcionarioId === f.id);
      if (siadapDoFunc.length === 0 && historicoDoFunc.length === 0) {
        mapa[f.id] = 'sem_registos';
        return;
      }
      const { periodo } = calcularLimiarFuncionario(siadapDoFunc, historicoDoFunc, limiarPos2025, limiarPre2023, limiar2023_24, escalas);
      mapa[f.id] = periodo as PeriodoEscala;
    });
    return mapa;
  }, [allFuncionarios, registosSiadap, registosHistorico, settings, escalas]);

  /** Limiar efetivo: se regra especial ativa → 6 pts; caso contrário → limiar calculado dinamicamente */
  const limiarEfetivoPorFuncionario = useMemo(() => {
    const mapa: Record<string, number> = {};
    // Garantir valores seguros — nunca undefined/NaN
    const limiarPos2025  = settings.pontos_progressao        || 8;
    const limiarPre2023  = settings.pontos_progressao_pre2025 || 10;
    const limiar2023_24  = settings.pontos_progressao_2023    || 8;

    allFuncionarios.forEach((f) => {
      if (regraEspecialPorFuncionario[f.id]) {
        mapa[f.id] = PONTOS_REGRA_18_ANOS;
      } else {
        const siadapDoFunc   = registosSiadap.filter((r) => r.funcionarioId === f.id);
        const historicoDoFunc = registosHistorico.filter((r) => r.funcionarioId === f.id);
        const { limiar } = calcularLimiarFuncionario(
          siadapDoFunc,
          historicoDoFunc,
          limiarPos2025,
          limiarPre2023,
          limiar2023_24,
          escalas
        );
        mapa[f.id] = limiar;
      }
    });
    return mapa;
  }, [allFuncionarios, regraEspecialPorFuncionario, registosSiadap, registosHistorico, settings.pontos_progressao, settings.pontos_progressao_pre2025, settings.pontos_progressao_2023, escalas]);

  const filtered = useMemo(() => {
    return allFuncionarios.filter((f) => {
      const matchSearch = search === '' || f.nome.toLowerCase().includes(search.toLowerCase()) || f.id.includes(search);
      const matchCarreira = carreiraFilter === '' || f.carreira === carreiraFilter;
      const matchSexo = sexoFilter === '' || f.sexo === sexoFilter;
      const matchAvaliacao = avaliacaoFilter === '' || f.avaliacao === avaliacaoFilter;
      let matchEleg = true;
      if (elegibilidadeFilter !== 'todos') {
        const pontos = pontosPorFuncionario[f.id] ?? 0;
        const limiar = limiarEfetivoPorFuncionario[f.id] ?? settings.pontos_progressao;
        const prog = calcularProgressao(pontos, f.nivelAtual, limiar);
        if (elegibilidadeFilter === 'elegiveis') matchEleg = prog.status === 'pronto';
        else if (elegibilidadeFilter === 'proximos') matchEleg = prog.status === 'proximo';
        else if (elegibilidadeFilter === 'em_progresso') matchEleg = prog.status === 'em_progresso';
      }
      return matchSearch && matchCarreira && matchSexo && matchAvaliacao && matchEleg;
    });
  }, [allFuncionarios, search, carreiraFilter, sexoFilter, avaliacaoFilter, elegibilidadeFilter, pontosPorFuncionario, limiarEfetivoPorFuncionario, settings.pontos_progressao]);

  // Funcionários elegíveis para 1ª progressão (>= limiar efetivo, inclui regra especial 18 anos)
  const todosElegiveis = useMemo(() => {
    return allFuncionarios.filter((f) => {
      const pontos = pontosPorFuncionario[f.id] ?? 0;
      // Nunca usar fallback genérico — o limiar efetivo já está calculado corretamente
      const limiar = limiarEfetivoPorFuncionario[f.id] ?? (settings.pontos_progressao_pre2025 || 10);
      const prog = calcularProgressao(pontos, f.nivelAtual, limiar);
      return prog.status === 'pronto';
    });
  }, [allFuncionarios, pontosPorFuncionario, limiarEfetivoPorFuncionario, settings.pontos_progressao_pre2025]);

  // Funcionários com pontos para 2ª progressão consecutiva (>= 2x limiar efetivo)
  const elegiveisSegundaProgressao = useMemo(() => {
    return allFuncionarios.filter((f) => {
      const pontos = pontosPorFuncionario[f.id] ?? 0;
      const limiar = limiarEfetivoPorFuncionario[f.id] ?? (settings.pontos_progressao_pre2025 || 10);
      return pontos >= limiar * 2;
    });
  }, [allFuncionarios, pontosPorFuncionario, limiarEfetivoPorFuncionario, settings.pontos_progressao_pre2025]);

  const elegiveisPendentes = useMemo(() => {
    return todosElegiveis.filter((f) => !dismissedIds.has(f.id));
  }, [todosElegiveis, dismissedIds]);

  const elegiveisSegundaPendentes = useMemo(() => {
    return elegiveisSegundaProgressao.filter((f) => !dismissedDuplosIds.has(f.id));
  }, [elegiveisSegundaProgressao, dismissedDuplosIds]);

  const hasFilters = search || carreiraFilter || sexoFilter || avaliacaoFilter;

  function clearFilters() { setSearch(''); setCarreiraFilter(''); setSexoFilter(''); setAvaliacaoFilter(''); setElegibilidadeFilter('todos'); }
  function dismissOne(id: string) { setDismissedIds((prev) => new Set([...prev, id])); }
  function dismissAll() { setDismissedIds(new Set(allFuncionarios.map((f) => f.id))); }
  function dismissDuplo(id: string) { setDismissedDuplosIds((prev) => new Set([...prev, id])); }
  function dismissAllDuplos() { setDismissedDuplosIds(new Set(allFuncionarios.map((f) => f.id))); }
  function showSuccess(msg: string) { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(''), 4000); }

  /** Agrupamento por carreira para o modo "por carreira" */
  const porCarreira = useMemo(() => {
    const grupos: Record<string, Funcionario[]> = {};
    filtered.forEach((f) => {
      if (!grupos[f.carreira]) grupos[f.carreira] = [];
      grupos[f.carreira].push(f);
    });
    // Ordenar: grupos com elegíveis primeiro, depois por nome
    return Object.entries(grupos).sort(([a, ga], [b, gb]) => {
      const eleA = ga.filter((f) => {
        const pontos = pontosPorFuncionario[f.id] ?? 0;
        const limiar = limiarEfetivoPorFuncionario[f.id] ?? settings.pontos_progressao;
        return pontos >= limiar;
      }).length;
      const eleB = gb.filter((f) => {
        const pontos = pontosPorFuncionario[f.id] ?? 0;
        const limiar = limiarEfetivoPorFuncionario[f.id] ?? settings.pontos_progressao;
        return pontos >= limiar;
      }).length;
      if (eleB !== eleA) return eleB - eleA;
      return a.localeCompare(b);
    });
  }, [filtered, pontosPorFuncionario, limiarEfetivoPorFuncionario, settings.pontos_progressao]);

  // Só considera carregado quando AMBOS os contextos terminaram E as definições estão prontas
  const isLoading = loading || loadingSiadap || loadingSettings;

  return (
    <>
      <main className="flex-1 px-8 py-6 max-w-[1400px] w-full mx-auto">

        {/* ── BANNER DE BOAS-VINDAS ── */}
        {(() => {
          const now = new Date();
          const hora = now.getHours();
          const saudacao = hora < 12 ? 'Bom dia' : hora < 19 ? 'Boa tarde' : 'Boa noite';
          const primeiroNome = profile?.full_name?.split(' ')[0] ?? '';
          const dataFormatada = now.toLocaleDateString('pt-PT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
          const dataCapitalizada = dataFormatada.charAt(0).toUpperCase() + dataFormatada.slice(1);
          return (
            <div className="mb-6 flex items-center justify-between gap-4 bg-white border border-zinc-100 rounded-xl px-5 py-4">
              <div className="flex items-center gap-4 min-w-0">
                {/* Avatar inicial */}
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-zinc-900 text-white text-sm font-bold flex-shrink-0">
                  {primeiroNome ? primeiroNome.charAt(0).toUpperCase() : <i className="ri-user-line text-sm" />}
                </div>
                <div className="min-w-0">
                  <p className="text-[15px] font-bold text-zinc-900 leading-tight truncate">
                    {saudacao}{primeiroNome ? `, ${primeiroNome}` : ''}!
                  </p>
                  <p className="text-[12px] text-zinc-400 mt-0.5 truncate">
                    {settings.nome_camara} &middot; {dataCapitalizada}
                  </p>
                </div>
              </div>
              {/* Indicador de ciclo ativo */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-zinc-600 bg-zinc-50 border border-zinc-100 px-3 py-1.5 rounded-full whitespace-nowrap">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse inline-block" />
                  Ciclo {settings.ciclo_siadap} ativo
                </span>
              </div>
            </div>
          );
        })()}

        {/* ── PAGE HEADER MODERNO ── */}
        <div className="mb-7">
          {/* Barra superior com título + ações */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
            <div className="flex items-center gap-4">
              {/* Ícone decorativo */}
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-900 flex-shrink-0">
                <i className="ri-bar-chart-line text-white text-lg" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">SIADAP · Ciclo 2023–2024</span>
                </div>
                <h1 className="text-[20px] font-bold text-zinc-900 tracking-tight leading-tight">Progressões</h1>
              </div>
            </div>

            {/* Ações */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {hasFilters && (
                <button onClick={clearFilters} className="whitespace-nowrap flex items-center gap-1.5 text-[12px] text-zinc-400 hover:text-zinc-700 transition-colors cursor-pointer px-2 py-1.5">
                  <i className="ri-close-circle-line text-sm" />
                  Limpar filtros
                </button>
              )}
              {!isLoading && todosElegiveis.length > 0 && (
                <>
                  <button
                    onClick={() => setAlertasModal(true)}
                    className="whitespace-nowrap flex items-center gap-1.5 text-[12px] font-medium border border-zinc-200 bg-white text-zinc-600 hover:text-zinc-900 hover:border-zinc-300 px-3 py-2 rounded-lg transition-all cursor-pointer"
                  >
                    <i className="ri-notification-3-line text-sm" />
                    Alertas
                  </button>
                  <button
                    onClick={() => setEmailModal(true)}
                    className="whitespace-nowrap flex items-center gap-1.5 text-[12px] font-medium border border-zinc-200 bg-white text-zinc-600 hover:text-zinc-900 hover:border-zinc-300 px-3 py-2 rounded-lg transition-all cursor-pointer"
                  >
                    <i className="ri-mail-send-line text-sm" />
                    Enviar Alerta
                  </button>
                  <button
                    onClick={() => setExportModal(true)}
                    className="whitespace-nowrap flex items-center gap-1.5 text-[12px] font-medium border border-zinc-200 bg-white text-zinc-600 hover:text-zinc-900 hover:border-zinc-300 px-3 py-2 rounded-lg transition-all cursor-pointer"
                  >
                    <i className="ri-file-pdf-line text-sm" />
                    Exportar PDF
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Linha de contexto / meta info */}
          <div className="flex items-center gap-2 flex-wrap mb-4">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-zinc-500 bg-white border border-zinc-100 px-2.5 py-1 rounded-full">
              <i className="ri-calendar-check-line text-xs text-zinc-400" />
              Avaliação 2023–2024
            </span>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-zinc-500 bg-white border border-zinc-100 px-2.5 py-1 rounded-full">
              <i className="ri-group-line text-xs text-zinc-400" />
              {allFuncionarios.length} trabalhadores
            </span>
            {!isLoading && todosElegiveis.length > 0 && (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse inline-block" />
                {todosElegiveis.length} elegíve{todosElegiveis.length === 1 ? 'l' : 'is'} para progressão
              </span>
            )}
            {!isLoading && elegiveisSegundaProgressao.length > 0 && (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-100 px-2.5 py-1 rounded-full">
                <i className="ri-repeat-line text-xs" />
                {elegiveisSegundaProgressao.length} dupla progressão
              </span>
            )}
          </div>

          {/* ── BARRA DE PROGRESSO DO CICLO ── */}
          {!isLoading && allFuncionarios.length > 0 && (
            <div className="bg-white border border-zinc-100 rounded-xl px-5 py-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <i className="ri-bar-chart-grouped-line text-zinc-400 text-sm" />
                  <span className="text-[12px] font-semibold text-zinc-600">Progresso do Ciclo 2023–2024</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[11px] text-zinc-400">
                    {allFuncionarios.length > 0
                      ? `${((todosElegiveis.length / allFuncionarios.length) * 100).toFixed(0)}% elegíveis`
                      : '—'}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-zinc-500 bg-zinc-50 border border-zinc-100 px-2 py-0.5 rounded-full">
                    <i className="ri-time-line text-[10px] text-zinc-400" />
                    Ciclo termina Dez 2025
                  </span>
                </div>
              </div>
              {/* Barra segmentada */}
              <div className="h-2.5 bg-zinc-100 rounded-full overflow-hidden flex gap-px">
                {allFuncionarios.length > 0 && (() => {
                  const total = allFuncionarios.length;
                  const elegiveisPct = (todosElegiveis.length / total) * 100;
                  const duplosPct = (elegiveisSegundaProgressao.length / total) * 100;
                  const restoPct = Math.max(0, 100 - elegiveisPct);
                  return (
                    <>
                      {duplosPct > 0 && <div className="h-full bg-amber-400 transition-all" style={{ width: `${duplosPct}%` }} />}
                      {(elegiveisPct - duplosPct) > 0 && <div className="h-full bg-emerald-400 transition-all" style={{ width: `${elegiveisPct - duplosPct}%` }} />}
                      {restoPct > 0 && <div className="h-full bg-zinc-200 transition-all" style={{ width: `${restoPct}%` }} />}
                    </>
                  );
                })()}
              </div>
              {/* Legenda */}
              <div className="flex items-center gap-4 mt-2.5 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" />
                  <span className="text-[11px] text-zinc-500">{todosElegiveis.length} elegíveis</span>
                </div>
                {elegiveisSegundaProgressao.length > 0 && (
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />
                    <span className="text-[11px] text-zinc-500">{elegiveisSegundaProgressao.length} dupla progressão</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-zinc-200 inline-block" />
                  <span className="text-[11px] text-zinc-500">{allFuncionarios.length - todosElegiveis.length} em progresso</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── STATS CARDS ── */}
        {!isLoading && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {/* Total */}
            <div className="bg-white rounded-xl border border-zinc-100 p-5 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Total</span>
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-zinc-50 border border-zinc-100">
                  <i className="ri-group-line text-zinc-500 text-sm" />
                </div>
              </div>
              <div>
                <p className="text-[28px] font-bold text-zinc-900 leading-none">{allFuncionarios.length}</p>
                <p className="text-[12px] text-zinc-400 mt-1.5">Funcionários registados</p>
              </div>
              {/* mini barra de progresso decorativa */}
              <div className="h-1 bg-zinc-100 rounded-full overflow-hidden">
                <div className="h-full bg-zinc-300 rounded-full" style={{ width: '100%' }} />
              </div>
            </div>

            {/* Elegíveis */}
            <div className="bg-white rounded-xl border border-emerald-100 p-5 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-emerald-600 uppercase tracking-widest">Elegíveis</span>
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-50">
                  <i className="ri-arrow-up-circle-line text-emerald-600 text-sm" />
                </div>
              </div>
              <div>
                <p className="text-[28px] font-bold text-zinc-900 leading-none">{todosElegiveis.length}</p>
                <p className="text-[12px] text-zinc-400 mt-1.5">Prontos para progressão</p>
              </div>
              <div className="h-1 bg-emerald-50 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-400 rounded-full transition-all"
                  style={{ width: allFuncionarios.length > 0 ? `${(todosElegiveis.length / allFuncionarios.length) * 100}%` : '0%' }}
                />
              </div>
            </div>

            {/* Dupla progressão */}
            <div className="bg-white rounded-xl border border-amber-100 p-5 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-amber-600 uppercase tracking-widest">Dupla</span>
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50">
                  <i className="ri-repeat-line text-amber-600 text-sm" />
                </div>
              </div>
              <div>
                <p className="text-[28px] font-bold text-zinc-900 leading-none">{elegiveisSegundaProgressao.length}</p>
                <p className="text-[12px] text-zinc-400 mt-1.5">2ª progressão consecutiva</p>
              </div>
              <div className="h-1 bg-amber-50 rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-400 rounded-full transition-all"
                  style={{ width: allFuncionarios.length > 0 ? `${(elegiveisSegundaProgressao.length / allFuncionarios.length) * 100}%` : '0%' }}
                />
              </div>
            </div>

            {/* Regra 18 anos */}
            <div className="bg-white rounded-xl border border-zinc-100 p-5 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Regra 18 anos</span>
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-zinc-50 border border-zinc-100">
                  <i className="ri-award-line text-zinc-500 text-sm" />
                </div>
              </div>
              <div>
                <p className="text-[28px] font-bold text-zinc-900 leading-none">
                  {Object.values(regraEspecialPorFuncionario).filter(Boolean).length}
                </p>
                <p className="text-[12px] text-zinc-400 mt-1.5">Regra especial 2024</p>
              </div>
              <div className="h-1 bg-zinc-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-zinc-400 rounded-full transition-all"
                  style={{ width: allFuncionarios.length > 0 ? `${(Object.values(regraEspecialPorFuncionario).filter(Boolean).length / allFuncionarios.length) * 100}%` : '0%' }}
                />
              </div>
            </div>
          </div>
        )}

        {successMsg && (
          <div className="mb-5 flex items-center gap-3 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[13px] px-4 py-3 rounded-xl">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-checkbox-circle-fill text-sm" /></div>
            {successMsg}
          </div>
        )}

        {/* ── GRÁFICO DE EVOLUÇÃO ── */}
        {!isLoading && <div className="mb-6"><EvolucaoPontosChart /></div>}

        {/* ── ALERTAS COMPACTOS ── */}
        {!isLoading && (elegiveisSegundaPendentes.length > 0 || elegiveisPendentes.length > 0) && (
          <div className="mb-5 space-y-2.5">

            {/* Alerta dupla progressão */}
            {elegiveisSegundaPendentes.length > 0 && (
              <div className="rounded-xl border border-amber-200/80 overflow-hidden" style={{ background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)' }}>
                {/* Header do alerta */}
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-6 h-6 flex items-center justify-center bg-amber-500 rounded-md text-white flex-shrink-0">
                      <i className="ri-repeat-line text-xs" />
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[12px] font-bold text-amber-900">
                        {elegiveisSegundaPendentes.length === 1 ? '1 funcionário' : `${elegiveisSegundaPendentes.length} funcionários`}
                      </span>
                      <span className="text-[12px] text-amber-700">com pontos para dupla progressão</span>
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-600 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded-full">
                        ≥ 2× limiar
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => setNotifDuplosCollapsed((v) => !v)}
                      className="whitespace-nowrap w-7 h-7 flex items-center justify-center text-amber-500 hover:text-amber-800 hover:bg-amber-100 rounded-lg transition-colors cursor-pointer"
                      title={notifDuplosCollapsed ? 'Expandir' : 'Recolher'}
                    >
                      <i className={`${notifDuplosCollapsed ? 'ri-arrow-down-s-line' : 'ri-arrow-up-s-line'} text-sm`} />
                    </button>
                    <button
                      onClick={dismissAllDuplos}
                      className="whitespace-nowrap w-7 h-7 flex items-center justify-center text-amber-500 hover:text-amber-800 hover:bg-amber-100 rounded-lg transition-colors cursor-pointer"
                      title="Dispensar todos"
                    >
                      <i className="ri-check-double-line text-sm" />
                    </button>
                  </div>
                </div>
                {/* Lista compacta */}
                {!notifDuplosCollapsed && (
                  <div className="border-t border-amber-200/60 divide-y divide-amber-100/80 max-h-52 overflow-y-auto">
                    {elegiveisSegundaPendentes.map((f) => {
                      const pontos = pontosPorFuncionario[f.id] ?? 0;
                      const limiar = limiarEfetivoPorFuncionario[f.id] ?? settings.pontos_progressao;
                      const progressoesConsecutivas = Math.floor(pontos / limiar);
                      return (
                        <div key={f.id} className="flex items-center justify-between px-4 py-2.5 bg-white/60 hover:bg-white/90 transition-colors">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="w-6 h-6 flex items-center justify-center bg-amber-100 rounded-full text-amber-600 flex-shrink-0 text-xs font-bold">
                              {progressoesConsecutivas}×
                            </div>
                            <div className="min-w-0">
                              <p className="text-[12px] font-semibold text-zinc-800 truncate">{f.nome}</p>
                              <p className="text-[10px] text-zinc-400">{f.carreira} · Nível {f.nivelAtual} · <span className="font-semibold text-amber-600">{pontos} pts</span></p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <button
                              onClick={() => setProgressaoModal(f)}
                              className="whitespace-nowrap flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-100 hover:bg-amber-200 px-2.5 py-1 rounded-md transition-colors cursor-pointer"
                            >
                              <i className="ri-arrow-up-circle-line text-xs" />
                              Registar
                            </button>
                            <button
                              onClick={() => dismissDuplo(f.id)}
                              className="whitespace-nowrap w-5 h-5 flex items-center justify-center text-amber-300 hover:text-amber-600 rounded transition-colors cursor-pointer"
                            >
                              <i className="ri-close-line text-xs" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Alerta 1ª progressão */}
            {elegiveisPendentes.length > 0 && (
              <div className="rounded-xl border border-emerald-200/80 overflow-hidden" style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)' }}>
                {/* Header do alerta */}
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-6 h-6 flex items-center justify-center bg-emerald-500 rounded-md text-white flex-shrink-0">
                      <i className="ri-arrow-up-circle-line text-xs" />
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[12px] font-bold text-emerald-900">
                        {elegiveisPendentes.length === 1 ? '1 funcionário' : `${elegiveisPendentes.length} funcionários`}
                      </span>
                      <span className="text-[12px] text-emerald-700">elegíve{elegiveisPendentes.length === 1 ? 'l' : 'is'} para progressão</span>
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 bg-emerald-100 border border-emerald-200 px-1.5 py-0.5 rounded-full">
                        <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse inline-block" />
                        Ação recomendada
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => setNotifCollapsed((v) => !v)}
                      className="whitespace-nowrap w-7 h-7 flex items-center justify-center text-emerald-500 hover:text-emerald-800 hover:bg-emerald-100 rounded-lg transition-colors cursor-pointer"
                      title={notifCollapsed ? 'Expandir' : 'Recolher'}
                    >
                      <i className={`${notifCollapsed ? 'ri-arrow-down-s-line' : 'ri-arrow-up-s-line'} text-sm`} />
                    </button>
                    <button
                      onClick={dismissAll}
                      className="whitespace-nowrap w-7 h-7 flex items-center justify-center text-emerald-500 hover:text-emerald-800 hover:bg-emerald-100 rounded-lg transition-colors cursor-pointer"
                      title="Dispensar todos"
                    >
                      <i className="ri-check-double-line text-sm" />
                    </button>
                  </div>
                </div>
                {/* Lista compacta */}
                {!notifCollapsed && (
                  <div className="border-t border-emerald-200/60 divide-y divide-emerald-100/80 max-h-64 overflow-y-auto">
                    {elegiveisPendentes.map((f) => {
                      const pontos = pontosPorFuncionario[f.id] ?? 0;
                      const limiar = limiarEfetivoPorFuncionario[f.id] ?? settings.pontos_progressao;
                      const prog = calcularProgressao(pontos, f.nivelAtual, limiar);
                      const temRegraEspecial = regraEspecialPorFuncionario[f.id] ?? false;
                      return (
                        <div key={f.id} className={`flex items-center justify-between px-4 py-2.5 transition-colors ${temRegraEspecial ? 'bg-amber-50/60 hover:bg-amber-50' : 'bg-white/60 hover:bg-white/90'}`}>
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 ${temRegraEspecial ? 'bg-amber-100 text-amber-600' : 'bg-emerald-100 text-emerald-600'}`}>
                              <i className={`${temRegraEspecial ? 'ri-award-fill' : 'ri-arrow-up-circle-fill'} text-xs`} />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <p className="text-[12px] font-semibold text-zinc-800 truncate">{f.nome}</p>
                                {temRegraEspecial && (
                                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold whitespace-nowrap cursor-help" title="18+ anos de serviço a 30/08/2023">
                                    <i className="ri-award-line text-[9px]" />
                                    18 anos
                                  </span>
                                )}
                                {funcionariosComAcelerador.has(f.id) && (
                                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-zinc-100 border border-zinc-200 text-zinc-500 text-[10px] font-semibold whitespace-nowrap cursor-help" title="Funcionário já usufruiu do Acelerador Remuneratório numa progressão anterior">
                                    <i className="ri-flashlight-line text-[9px]" />
                                    Acelerador utilizado
                                  </span>
                                )}
                              </div>
                              <p className="text-[10px] text-zinc-400">{f.carreira} · Nível {f.nivelAtual} → {f.nivelAtual + 1} · <span className={`font-semibold ${temRegraEspecial ? 'text-amber-600' : 'text-emerald-600'}`}>{prog.pontosAtuais} pts</span></p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <button
                              onClick={() => setProgressaoModal(f)}
                              className={`whitespace-nowrap flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md transition-colors cursor-pointer ${temRegraEspecial ? 'text-amber-700 bg-amber-100 hover:bg-amber-200' : 'text-emerald-700 bg-emerald-100 hover:bg-emerald-200'}`}
                            >
                              <i className="ri-arrow-up-circle-line text-xs" />
                              Registar
                            </button>
                            <button
                              onClick={() => dismissOne(f.id)}
                              className="whitespace-nowrap w-5 h-5 flex items-center justify-center text-emerald-300 hover:text-emerald-600 rounded transition-colors cursor-pointer"
                            >
                              <i className="ri-close-line text-xs" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {isLoading ? (
          <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3 text-zinc-300">
            <div className="w-8 h-8 flex items-center justify-center"><i className="ri-loader-4-line text-3xl animate-spin" /></div>
            <p className="text-[13px] text-zinc-400">A carregar dados...</p>
          </div>
        ) : (
          <>
            {/* Filtros de elegibilidade rápidos */}
            <div className="mb-3 flex items-center gap-2 flex-wrap">
              <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest flex-shrink-0">Filtrar:</span>
              {([
                { key: 'todos', label: 'Todos', count: allFuncionarios.length, activeBg: 'bg-zinc-900', activeText: 'text-white', inactiveText: 'text-zinc-500' },
                { key: 'elegiveis', label: 'Elegíveis', count: todosElegiveis.length, activeBg: 'bg-emerald-600', activeText: 'text-white', inactiveText: 'text-emerald-600' },
                { key: 'proximos', label: 'Próximos', count: allFuncionarios.filter((f) => { const p = calcularProgressao(pontosPorFuncionario[f.id] ?? 0, f.nivelAtual, limiarEfetivoPorFuncionario[f.id] ?? settings.pontos_progressao); return p.status === 'proximo'; }).length, activeBg: 'bg-amber-500', activeText: 'text-white', inactiveText: 'text-amber-600' },
                { key: 'em_progresso', label: 'Em Progresso', count: allFuncionarios.filter((f) => { const p = calcularProgressao(pontosPorFuncionario[f.id] ?? 0, f.nivelAtual, limiarEfetivoPorFuncionario[f.id] ?? settings.pontos_progressao); return p.status === 'em_progresso'; }).length, activeBg: 'bg-zinc-500', activeText: 'text-white', inactiveText: 'text-zinc-400' },
              ] as { key: typeof elegibilidadeFilter; label: string; count: number; activeBg: string; activeText: string; inactiveText: string }[]).map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setElegibilidadeFilter(opt.key)}
                  className={`whitespace-nowrap flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
                    elegibilidadeFilter === opt.key
                      ? `${opt.activeBg} ${opt.activeText}`
                      : `bg-zinc-100 ${opt.inactiveText} hover:bg-zinc-200`
                  }`}
                >
                  {opt.label}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${elegibilidadeFilter === opt.key ? 'bg-white/20 text-white' : 'bg-white text-zinc-500'}`}>
                    {opt.count}
                  </span>
                </button>
              ))}
            </div>

            {/* Secção de tabela */}
            <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Trabalhadores</p>
                <p className="text-[13px] font-semibold text-zinc-700 mt-0.5">
                  {filtered.length === allFuncionarios.length
                    ? `${allFuncionarios.length} funcionários`
                    : `${filtered.length} de ${allFuncionarios.length} funcionários`}
                </p>
              </div>
              {/* Toggle de modo de visualização */}
              <div className="flex items-center gap-1 bg-zinc-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('lista')}
                  className={`whitespace-nowrap flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md transition-all cursor-pointer ${
                    viewMode === 'lista'
                      ? 'bg-white text-zinc-900 shadow-sm'
                      : 'text-zinc-500 hover:text-zinc-700'
                  }`}
                >
                  <i className="ri-list-check text-sm" />
                  Lista
                </button>
                <button
                  onClick={() => setViewMode('carreira')}
                  className={`whitespace-nowrap flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md transition-all cursor-pointer ${
                    viewMode === 'carreira'
                      ? 'bg-white text-zinc-900 shadow-sm'
                      : 'text-zinc-500 hover:text-zinc-700'
                  }`}
                >
                  <i className="ri-briefcase-line text-sm" />
                  Por Carreira
                </button>
              </div>
            </div>
            <FilterBar search={search} onSearchChange={setSearch} carreiraFilter={carreiraFilter} onCarreiraChange={setCarreiraFilter} sexoFilter={sexoFilter} onSexoChange={setSexoFilter} avaliacaoFilter={avaliacaoFilter} onAvaliacaoChange={setAvaliacaoFilter} carreiras={carreiras} />

            {/* Banner de ação rápida quando filtro de elegíveis está ativo */}
            {elegibilidadeFilter === 'elegiveis' && filtered.length > 0 && (
              <div className="flex items-center justify-between gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 flex items-center justify-center bg-emerald-500 rounded-lg text-white flex-shrink-0">
                    <i className="ri-arrow-up-circle-line text-sm" />
                  </div>
                  <div>
                    <p className="text-[13px] font-bold text-emerald-900">{filtered.length} funcionário{filtered.length !== 1 ? 's' : ''} elegíve{filtered.length !== 1 ? 'is' : 'l'} para progressão</p>
                    <p className="text-[11px] text-emerald-700">Clica em "Progressão" em cada linha para registar individualmente</p>
                  </div>
                </div>
                <span className="text-[11px] font-semibold text-emerald-600 bg-emerald-100 border border-emerald-200 px-2.5 py-1 rounded-full flex-shrink-0">
                  Ação recomendada
                </span>
              </div>
            )}

            {viewMode === 'lista' ? (
              <FuncionariosTable funcionarios={filtered} pontosPorFuncionario={pontosPorFuncionario} limiarPorFuncionario={limiarEfetivoPorFuncionario} regraEspecialPorFuncionario={regraEspecialPorFuncionario} periodoPorFuncionario={periodoPorFuncionario} prepavPorFuncionario={prepavPorFuncionario} onRegistarProgressao={(f) => setProgressaoModal(f)} />
            ) : (
              <div className="space-y-3">
                {porCarreira.length === 0 ? (
                  <div className="bg-white rounded-xl border border-zinc-100 py-16 flex flex-col items-center gap-3 text-zinc-300">
                    <div className="w-10 h-10 flex items-center justify-center">
                      <i className="ri-search-line text-3xl" />
                    </div>
                    <p className="text-[13px] text-zinc-400">Nenhum funcionário encontrado.</p>
                  </div>
                ) : (
                  porCarreira.map(([carreira, funcs]) => (
                    <CarreiraGroup
                      key={carreira}
                      carreira={carreira}
                      funcionarios={funcs}
                      pontosPorFuncionario={pontosPorFuncionario}
                      limiarPorFuncionario={limiarEfetivoPorFuncionario}
                      regraEspecialPorFuncionario={regraEspecialPorFuncionario}
                      onRegistar={(f) => setProgressaoModal(f)}
                    />
                  ))
                )}
              </div>
            )}
          </>
        )}
      </main>



      {progressaoModal && (
        <RegistarProgressaoModal
          funcionario={progressaoModal}
          pontosCalculados={pontosPorFuncionario[progressaoModal.id] ?? progressaoModal.pontosAtuais ?? 0}
          limiarEfetivo={limiarEfetivoPorFuncionario[progressaoModal.id]}
          jaUsouAcelerador={funcionariosComAcelerador.has(progressaoModal.id)}
          onClose={() => setProgressaoModal(null)}
          onSuccess={(nome) => {
            dismissOne(progressaoModal.id);
            setProgressaoModal(null);
            showSuccess(`Progressão de ${nome} registada com sucesso!`);
            reloadSiadap();
          }}
        />
      )}
      {emailModal && <EmailAlertModal elegiveis={todosElegiveis} pontosPorFuncionario={pontosPorFuncionario} onClose={() => setEmailModal(false)} />}
      {alertasModal && <AlertasAutomaticosModal elegiveis={todosElegiveis} pontosPorFuncionario={pontosPorFuncionario} onClose={() => setAlertasModal(false)} />}
      {exportModal && (
        <ExportPdfModal
          elegiveis={todosElegiveis}
          pontosPorFuncionario={pontosPorFuncionario}
          limiarPorFuncionario={limiarEfetivoPorFuncionario}
          regraEspecialPorFuncionario={regraEspecialPorFuncionario}
          onClose={() => setExportModal(false)}
        />
      )}
    </>
  );
}
